import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_Plugins = () => {
  return (
    <div className="html-container">
      <h1>HTML Plug-ins – Comprehensive Guide</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction to HTML Plug-ins</h2>
        <p>
          HTML <strong>plug-ins</strong> are external applications that extend a
          browser’s functionality. They allow the display of content types that
          browsers don’t natively support, such as PDFs, videos, Flash
          animations, or Java applets.
        </p>

        <p>
          Although HTML5 has replaced most plug-in functionality with native
          elements (<code>&lt;video&gt;</code>, <code>&lt;audio&gt;</code>,
          <code>&lt;canvas&gt;</code>, and <code>&lt;svg&gt;</code>), it’s
          essential to understand how legacy plug-ins worked and how to embed
          external content safely.
        </p>
      </section>

      {/* 2. The <object> Element */}
      <section className="html-section">
        <h2>2. The &lt;object&gt; Element</h2>
        <p>
          The <code>&lt;object&gt;</code> tag is used to embed multimedia or
          external resources, such as PDFs, Flash files, or web applications,
          directly into a webpage.
        </p>

        <pre className="code-block">
          <code>{`<object data="document.pdf" type="application/pdf" width="600" height="400">
  <p>Your browser does not support embedded PDFs. 
  <a href="document.pdf">Download the PDF</a> instead.</p>
</object>`}</code>
        </pre>

        <p>
          ✅ The <code>data</code> attribute specifies the resource path, and{" "}
          <code>type</code> defines the MIME type of the file.
        </p>
      </section>

      {/* 3. The <embed> Element */}
      <section className="html-section">
        <h2>3. The &lt;embed&gt; Element</h2>
        <p>
          The <code>&lt;embed&gt;</code> tag provides a simpler, self-closing
          alternative to the <code>&lt;object&gt;</code> element. It directly
          inserts external content or plug-ins into a webpage.
        </p>

        <pre className="code-block">
          <code>{`<embed src="animation.swf" width="500" height="300" type="application/x-shockwave-flash">

<!-- Example for PDF -->
<embed src="example.pdf" type="application/pdf" width="600" height="400">`}</code>
        </pre>

        <p>
          ⚠️ The <code>&lt;embed&gt;</code> element has no fallback content. If
          the plug-in or format isn’t supported, the element will simply fail to
          display anything.
        </p>
      </section>

      {/* 4. Common MIME Types */}
      <section className="html-section">
        <h2>4. Common MIME Types for Plug-ins</h2>
        <table className="styled-table">
          <thead>
            <tr>
              <th>File Type</th>
              <th>File Extension</th>
              <th>MIME Type</th>
              <th>Example Use</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>PDF Document</td>
              <td>.pdf</td>
              <td>application/pdf</td>
              <td>Embedding PDF documents</td>
            </tr>
            <tr>
              <td>Flash Movie</td>
              <td>.swf</td>
              <td>application/x-shockwave-flash</td>
              <td>Legacy Flash animations</td>
            </tr>
            <tr>
              <td>QuickTime Video</td>
              <td>.mov</td>
              <td>video/quicktime</td>
              <td>Legacy Apple videos</td>
            </tr>
            <tr>
              <td>Java Applet</td>
              <td>.class / .jar</td>
              <td>application/x-java-applet</td>
              <td>Interactive Java applications</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 5. Fallback Content */}
      <section className="html-section">
        <h2>5. Providing Fallback Content</h2>
        <p>
          Always provide alternative content in case the plug-in is unavailable
          or unsupported. This ensures accessibility and prevents blank content
          areas.
        </p>

        <pre className="code-block">
          <code>{`<object data="example.pdf" type="application/pdf" width="600" height="400">
  <p>It seems your browser can't display this file.
  <a href="example.pdf">Click here to download it</a>.</p>
</object>`}</code>
        </pre>
      </section>

      {/* 6. <iframe> as an Alternative */}
      <section className="html-section">
        <h2>6. Using &lt;iframe&gt; as a Safer Alternative</h2>
        <p>
          For modern web applications, the <code>&lt;iframe&gt;</code> element
          can be used to embed external content securely instead of legacy
          plug-ins.
        </p>

        <pre className="code-block">
          <code>{`<iframe src="document.pdf" width="600" height="400" title="PDF Viewer">
  Your browser does not support iframes.
</iframe>`}</code>
        </pre>

        <p>
          <code>&lt;iframe&gt;</code> elements offer better browser support and
          security than plug-in-based elements.
        </p>
      </section>

      {/* 7. Browser Support */}
      <section className="html-section">
        <h2>7. Browser Support and Deprecation</h2>
        <p>
          Modern browsers (Chrome, Edge, Firefox, Safari) have **deprecated**
          support for plug-ins like Flash and Java due to security risks.
        </p>

        <ul>
          <li>🔒 Flash and Java plug-ins are no longer supported.</li>
          <li>✅ Use native HTML5 elements for multimedia content.</li>
          <li>
            📄 PDFs and documents should be embedded with{" "}
            <code>&lt;object&gt;</code> or <code>&lt;iframe&gt;</code>.
          </li>
        </ul>
      </section>

      {/* 8. Best Practices */}
      <section className="html-section">
        <h2>8. Best Practices</h2>
        <ul>
          <li>Prefer HTML5 native tags over plug-ins whenever possible.</li>
          <li>
            Always include fallback text or links for unsupported browsers.
          </li>
          <li>
            Use secure content sources (HTTPS) when embedding third-party
            content.
          </li>
          <li>Avoid embedding obsolete technologies like Flash or Java applets.</li>
        </ul>
      </section>

      {/* 9. Summary */}
      <section className="html-section">
        <h2>9. Summary</h2>
        <ul>
          <li>
            <code>&lt;object&gt;</code> and <code>&lt;embed&gt;</code> were used
            to include external resources.
          </li>
          <li>Most plug-ins are obsolete in modern browsers.</li>
          <li>
            Use <code>&lt;iframe&gt;</code>, <code>&lt;video&gt;</code>, and{" "}
            <code>&lt;audio&gt;</code> instead for safe embedding.
          </li>
          <li>Always provide fallback and accessible alternatives.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_Plugins;
